import { getEnvVar } from '@/env';

import {
  prepareGoogleSignupConsent,
  recordGoogleSignupIntent,
} from './google-signup-consent-config';
import { storeBeforeRedirect } from './login-redirect-storage';
import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
} from './pkce';
import { PROVIDERS } from './providers';

import type { LoginCallbackAdapter } from './callback-adapter';
import type { LoginBrowserAttribution } from './login-browser-attribution';
import type { PolynomicClient } from './polynomic-client';
import type {
  Auth0LoginConfig,
  SocialOAuthFlowParams,
  SocialProviderConfig,
  TokenExchangeResponse,
} from './types';

function applyProviderAuthorizeParams(
  params: URLSearchParams,
  connection: string
): void {
  if (connection !== PROVIDERS.google.connection) return;
  params.set('prompt', 'select_account');
}

/**
 * Exchange an authorization code for tokens via Auth0's /oauth/token endpoint.
 * Shared by social login popup flow and desktop browser callback flow.
 */
export async function exchangeCodeForTokens(
  auth0: Auth0LoginConfig,
  params: Record<string, unknown>
): Promise<TokenExchangeResponse> {
  const data = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) {
      data.append(key, String(value));
    }
  }

  const response = await fetch(`https://${auth0.domain}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: data,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/** The user closed the login window/tab without completing the flow. */
export class SocialLoginAbandonedError extends Error {
  constructor() {
    super('Social login window closed before completing sign-in');
    this.name = 'SocialLoginAbandonedError';
  }
}

const AUTH_WINDOW_CLOSED_POLL_MS = 1000;
/** The callback page closes itself right after posting, so allow it to land. */
const AUTH_WINDOW_CLOSED_GRACE_MS = 2000;

/**
 * Initiate a social login flow (Google, Apple, etc.) via popup/window.
 * Opens the Auth0 authorize URL and listens for the callback.
 * Returns tokens on success. Rejects with SocialLoginAbandonedError when the
 * auth window is closed without completing the flow.
 *
 * Must be called synchronously from the user gesture (click handler): the
 * auth window is pre-opened before any await so popup blockers allow it,
 * then navigated once the PKCE params are ready.
 */
export function initiateSocialLogin(
  provider: SocialProviderConfig,
  auth0: Auth0LoginConfig,
  adapter: LoginCallbackAdapter,
  options: { loginHint?: string; connectionScopes?: string[] } = {}
): Promise<TokenExchangeResponse> {
  return new Promise(async (resolve, reject) => {
    let closedPollInterval: ReturnType<typeof setInterval> | undefined;
    const stopWatchingForClose = () => clearInterval(closedPollInterval);

    const preopenedWindow = adapter.preopenAuthWindow();

    try {
      const nonce = window.crypto.randomUUID();
      const baseState = generateState();
      const state = adapter.generateStatePrefix
        ? `${adapter.generateStatePrefix()}-${baseState}`
        : baseState;
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const redirectUri = adapter.getRedirectUri();

      sessionStorage.setItem(
        state,
        JSON.stringify({
          integration_provider_type: provider.connection.toUpperCase(),
          flow_type: 'SIGN_IN',
        })
      );

      const params = new URLSearchParams({
        client_id: auth0.clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        code_challenge_method: 'S256',
        code_challenge: codeChallenge,
        scope: 'openid profile email offline_access',
        state,
        connection: provider.connection,
        nonce,
        audience: auth0.audience,
      });
      applyProviderAuthorizeParams(params, provider.connection);
      const signupToolkits = options.connectionScopes?.length
        ? []
        : await prepareGoogleSignupConsent(params, provider.connection);
      if (options.connectionScopes?.length) {
        params.set('connection_scope', options.connectionScopes.join(','));
        params.set('access_type', 'offline');
        params.set('max_age', '0');
        params.set('prompt', 'consent');
      }
      if (options.loginHint) params.set('login_hint', options.loginHint);

      const authUrl = `https://${auth0.domain}/authorize?${params.toString()}`;

      let callbackReceived = false;
      const cleanup = adapter.listenForCallback(async (callbackParams) => {
        callbackReceived = true;
        cleanup();
        stopWatchingForClose();
        const code = callbackParams.get('code');
        const returnedState = callbackParams.get('state');
        if (returnedState) sessionStorage.removeItem(returnedState);

        if (!code) {
          // Auth0 redirects back with `error` / `error_description` instead of a code when it refuses the sign-in
          const authError =
            callbackParams.get('error_description') ??
            callbackParams.get('error');
          reject(new Error(authError ?? 'No authorization code received'));
          return;
        }

        try {
          const tokens = await exchangeCodeForTokens(auth0, {
            grant_type: 'authorization_code',
            code,
            client_id: auth0.clientId,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
          });
          await recordGoogleSignupIntent(tokens.access_token, signupToolkits);
          resolve(tokens);
        } catch (error) {
          reject(error);
        }
      });

      const authWindow = adapter.openAuthUrl(authUrl, preopenedWindow);
      if (authWindow) {
        closedPollInterval = setInterval(() => {
          if (!authWindow.closed) return;
          stopWatchingForClose();
          setTimeout(() => {
            if (callbackReceived) return;
            cleanup();
            sessionStorage.removeItem(state);
            reject(new SocialLoginAbandonedError());
          }, AUTH_WINDOW_CLOSED_GRACE_MS);
        }, AUTH_WINDOW_CLOSED_POLL_MS);
      }
    } catch (error) {
      preopenedWindow?.close();
      stopWatchingForClose();
      reject(error);
    }
  });
}

/**
 * Prepare a social OAuth flow for redirect-based login (used by DesktopLoginScreen).
 * Returns flow params without initiating the redirect.
 */
export async function prepareSocialOAuthFlow(options: {
  connection: string;
  auth0: Auth0LoginConfig;
  redirectUri: string;
  statePrefix?: string;
  loginHint?: string;
}): Promise<SocialOAuthFlowParams> {
  const nonce = window.crypto.randomUUID();
  const baseState = generateState();
  const state = options.statePrefix
    ? `${options.statePrefix}-${baseState}`
    : baseState;

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const params = new URLSearchParams({
    client_id: options.auth0.clientId,
    redirect_uri: options.redirectUri,
    response_type: 'code',
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
    scope: 'openid profile email offline_access',
    state,
    connection: options.connection,
    nonce,
    audience: options.auth0.audience,
  });
  applyProviderAuthorizeParams(params, options.connection);
  const googleSignupToolkits = await prepareGoogleSignupConsent(
    params,
    options.connection
  );
  if (options.loginHint) params.set('login_hint', options.loginHint);

  const authUrl = `https://${options.auth0.domain}/authorize?${params.toString()}`;

  return {
    codeVerifier,
    codeChallenge,
    state,
    nonce,
    authUrl,
    googleSignupToolkits,
  };
}

/**
 * Initiate a social OAuth redirect (used by DesktopLoginScreen in the browser).
 * Stores the code verifier and login attempt for the redirect, then redirects to Auth0.
 */
export async function initiateSocialOAuthRedirect(options: {
  connection: string;
  auth0: Auth0LoginConfig;
  redirectUri: string;
  statePrefix?: string;
  loginHint?: string;
  loginAttempt?: string;
  onError?: (error: Error) => void;
}): Promise<void> {
  try {
    const flowParams = await prepareSocialOAuthFlow(options);

    storeBeforeRedirect(flowParams.state, {
      codeVerifier: flowParams.codeVerifier,
      loginAttempt: options.loginAttempt,
      googleSignupToolkits: flowParams.googleSignupToolkits,
    });

    window.location.href = flowParams.authUrl;
  } catch (error) {
    console.error('Error initiating social OAuth redirect:', error);
    options.onError?.(error as Error);
    throw error;
  }
}

/**
 * Best-effort call to match the user's download attribution record.
 * Fire-and-forget — never awaited, errors are swallowed.
 *
 * @param opts.loginBrowserUserAgent - navigator.userAgent of the browser that
 *   completed the login. Passed across the deep-link handoff into Electron so
 *   the backend can match against the download fingerprint.
 * @param opts.loginBrowserCometFingerprint - Cometly browser fingerprint
 *   captured in the login-completing browser for attribution matching
 * @param opts.loginBrowserCometToken - Cometly tracking token captured in the
 *   login-completing browser for attribution matching
 */
export function matchAttribution(
  accessToken: string,
  opts?: LoginBrowserAttribution,
  polynomicClient?: PolynomicClient
): void {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };
  if (opts?.loginBrowserUserAgent) {
    headers['X-Login-Browser-User-Agent'] = opts.loginBrowserUserAgent;
  }
  if (opts?.loginBrowserCometFingerprint) {
    headers['X-Login-Browser-Comet-Fingerprint'] =
      opts.loginBrowserCometFingerprint;
  }
  if (opts?.loginBrowserCometToken) {
    headers['X-Login-Browser-Comet-Token'] = opts.loginBrowserCometToken;
  }
  if (polynomicClient) {
    headers['X-Polynomic-Client-Id'] = polynomicClient.clientId;
    headers['X-Polynomic-Client-Observed-At'] = polynomicClient.observedAt;
  }
  fetch(`${getEnvVar('VITE_API_URL')}/attribution/match`, {
    method: 'POST',
    headers,
  }).catch(() => {
    /* best effort */
  });
}
